import {Button, Stack} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import {config} from "../../../constant";
import DialogDecision from "./DialogDecision";
import DialogLinkCategories from "./DialogLinkCategories";
import UserContext from "../../../UserContext";
import {getOrdinal} from '../../../utils/formatNumber';
import {joinWithLastWord, getPlural} from '../../../utils/formatString';

export default function VoteResult() {
    const {user, setUser} = useContext(UserContext);
    const [voteResult, setVoteResult] = useState();
    const [categories, setCategories] = useState();
    const [people, setPeople] = useState();
    const [categoriesToSettle, setCategoriesToSettle] = useState();
    const [categoriesNotSettled, setCategoriesNotSettled] = useState();
    const [dialogDecisionOpen, setDialogDecisionOpen] = useState(false);
    const [dialogLinkOpen, setDialogLinkOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([fetch(`${config.BASE_URL}/votes/result`), fetch(`${config.BASE_URL}/people`), fetch(`${config.BASE_URL}/categories`)])
            .then(([resVotes, resPeople, resCategories]) => {
                if (!resVotes.ok) {
                    throw new Error(`${resVotes.status} ${resVotes.statusText}`)
                }
                if (resVotes.status === 204) {
                    return Promise.all([]);
                }
                if (!resPeople.ok) {
                    throw new Error(`${resPeople.status} ${resPeople.statusText}`)
                }
                if (!resCategories.ok) {
                    throw new Error(`${resCategories.status} ${resCategories.statusText}`)
                }
                return Promise.all([resVotes.json(), resPeople.json(), resCategories.json()]);
            }).then(([dataVotes, dataPeople, dataCategories]) => {
                if (dataVotes) {
                    setVoteResult(dataVotes);
                    setPeople(dataPeople);
                    setCategories(dataCategories);
                    updateCategoriesToSettle(dataCategories, dataVotes);
                }
            }).catch(error => {
                const message = 'Erreur dans la récupération des résultats de vote';
                console.error(message, error);
                navigate("/error", {state: {message: message}});
            });
    }, []);

    /**
     * Recherche des catégories à départager (plus d'un vainqueur)
     */
    const updateCategoriesToSettle = (categories, votes) => {
        const categoriesToSettleTemp = [];
        const categoriesNotSettledTemp = [];
        categories.forEach(category => {
            if (votes.winnersByCategory[category.id] != null && votes.winnersByCategory[category.id].length > 1) {
                categoriesToSettleTemp.push(category);

                // S'il n'y a pas eu de décision, il faut l'afficher
                if (votes.decisionsByCategory == null || votes.decisionsByCategory[category.id] == null) {
                    categoriesNotSettledTemp.push(category);
                }
            }
        });
        setCategoriesToSettle(categoriesToSettleTemp);
        setCategoriesNotSettled(categoriesNotSettledTemp);
    };

    const updateVotes = () => {
        fetch(`${config.BASE_URL}/votes/result`)
            .then(response => response.json())
            .then(dataVotes => {
                setVoteResult(dataVotes);
                updateCategoriesToSettle(categories, dataVotes);
            })
            .catch((error) => {
                const message = 'Erreur dans la récupération des votes';
                console.error(message, error);
                navigate("/error", {state: {message: message}});
            });
    };

    const getNames = (ids) => {
        const result = ids.map(id => people.find(person => person.id === id).firstName);
        return joinWithLastWord(result, ', ', 'et')
    };

    const displayVotes = (categoryId) => {
        const votes = voteResult.votesByCategory[categoryId];
        if (votes) {
            return (
                <ul>
                    {votes.map(vote => (
                    <li key={vote.id} style={{marginLeft: '2rem'}}>
                        {vote.voter.firstName} vote pour "{vote.nomination.reason}"<br />
                        Raison : {vote.reason}
                    </li>
                    ))}
                </ul>
            );
        }
        return <span>Aucun vote<br /></span>;
    };

    const displayWinners = (categoryId) => {
        const winners = voteResult.winnersByCategory[categoryId];
        if (!winners) {
            return <span>Aucun vote</span>;
        }
        switch(winners.length) {
            case 0:
                return <span>aucun vote</span>;
            case 1:
                return <span>{getNames(winners)}</span>;
            default:
                return <span>ex aequo entre {getNames(winners)}</span>;
        }
    };

    const displayDecision = (categoryId) => {
        const decision = voteResult.decisionsByCategory[categoryId];
        if (decision) {
            return <>
                <br /><b>Décision du président :</b> {decision.nominated ? decision.nominated.firstName : 'Tous'}
            </>
        }
        return null;
    };

    const displaySeconds = (categoryId) => {
        const seconds = voteResult.secondsByCategory[categoryId];
        if (seconds) {
            return <span>(suivi par {getNames(seconds)})</span>
        }
        return null;
    };

    const displayLosers = () => {
        const result = [];
        switch(voteResult.losers.length) {
            case 0:
                result.push(<span key={0}>Aucun vainqueur pour le moment</span>);
                break;
            case 1:
                result.push(<span key={0}>Le loser de l'année est {getNames(voteResult.losers)} avec {getPlural(voteResult.nbAwardsByPerson[voteResult.losers[0]], 'récompense')}</span>);
                break;
            default:
                result.push(<span key={0}>Les losers de l'année sont {getNames(voteResult.losers)} avec {getPlural(voteResult.nbAwardsByPerson[voteResult.losers[0]], 'récompense')}</span>);
        }
        if (categoriesNotSettled && categoriesNotSettled.length > 0) {
            result.push(<span key={1}> mais il reste des ex aequo dans les catégories </span>);
            result.push(joinWithLastWord(categoriesNotSettled.map(category => category.name)));
        } else if (voteResult.grandLosers && voteResult.grandLosers.length > 0) {
            if (voteResult.grandLosers.length === 1) {
                result.push(<span key={1}> et le Grand Loser est </span>);
            } else {
                result.push(<span key={1}> et les Grands Losers sont </span>);
            }
            result.push(getNames(voteResult.grandLosers));
        }
        result.push(<span>.</span>);
        return result;
    };

    const displayRanking = () => {
        const entries = Object.keys(voteResult.ranking);
        const result = [];
        entries.forEach(entry => {
            if (entry > 1) {
                result.push(<li key={entry}>{getOrdinal(parseInt(entry, 10))} : {getNames(voteResult.ranking[entry])}</li>);
            }
        });
        return result;
    };

    return (
        <>
            {voteResult ? (
                <>
                    {categories.map(category => (
                        <Box key={category.id}>
                            <h4 style={{marginBottom: 5}}>Catégorie {category.name}</h4>
                            {displayVotes(category.id)}
                            {voteResult.votesByCategory[category.id] && (
                                <div style={{marginTop: 5}}>
                                    <b>Résultats :</b> {displayWinners(category.id)} {displaySeconds(category.id)}
                                    {displayDecision(category.id)}
                                </div>
                            )}
                        </Box>
                    ))}
                    <Box sx={{mt: 3}}>{displayLosers()}</Box>
                    {displayRanking()}

                    <Stack direction="row" sx={{mt: 4}}>
                        {(categoriesToSettle && categoriesToSettle.length > 0) && (
                            <Button variant="contained" sx={{textTransform: 'none', mr: 2}} onClick={() => setDialogDecisionOpen(true)}>
                                Départager les ex aequo
                            </Button>
                        )}
                        {(user && user.isAdmin) && (
                            <Button variant="contained" sx={{textTransform: 'none'}} onClick={() => setDialogLinkOpen(true)}>
                                Créer une archive
                            </Button>
                        )}
                    </Stack>

                    {dialogDecisionOpen && (
                        <DialogDecision open={dialogDecisionOpen} onClose={() => {setDialogDecisionOpen(false); updateVotes();}} categories={categoriesToSettle} winnersByCategory={voteResult.winnersByCategory} people={people} decisionsByCategory={voteResult.decisionsByCategory}>
                        </DialogDecision>
                    )}

                    {dialogLinkOpen && (
                        <DialogLinkCategories open={dialogLinkOpen} onClose={() => setDialogLinkOpen(false)} onSave={() => {setDialogLinkOpen(false); console.log('Création archive OK');}}>
                        </DialogLinkCategories>
                    )}
                </>
            ) : (
                <>
                    <Box>
                        Aucun vote pour le moment.
                    </Box>
                    <Link to="/dashboard/vote/form">
                        <Button variant="contained" sx={{mt: 4, textTransform: 'none'}}>
                            Retour
                        </Button>
                    </Link>
                </>
            )}
        </>
    );
}